import React from 'react';
import { Award, CheckCircle, Shield, TrendingUp } from 'lucide-react';
import { trustSignals } from '../data/mock';

const TrustSection = ({ onOpenForm }) => {
  return (
    <section id="trust" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wide">Trust & Excellence</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              Proven Track Record of Success
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Our achievements speak for themselves. We've built our reputation on delivering results and exceeding client expectations.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="text-center bg-accent/30 rounded-lg p-8 hover:shadow-lg transition-all">
              <div className="flex justify-center mb-4">
                <div className="bg-secondary w-16 h-16 rounded-full flex items-center justify-center">
                  <Shield className="text-white" size={32} />
                </div>
              </div>
              <div className="text-4xl font-bold text-primary mb-2">{trustSignals.trademarksRegistered}</div>
              <div className="text-gray-600 font-medium">Trademarks Successfully Registered</div>
            </div>

            <div className="text-center bg-accent/30 rounded-lg p-8 hover:shadow-lg transition-all">
              <div className="flex justify-center mb-4">
                <div className="bg-secondary w-16 h-16 rounded-full flex items-center justify-center">
                  <CheckCircle className="text-white" size={32} />
                </div>
              </div>
              <div className="text-4xl font-bold text-primary mb-2">{trustSignals.approvalRate}</div>
              <div className="text-gray-600 font-medium">USPTO Approval Success Rate</div>
            </div>

            <div className="text-center bg-accent/30 rounded-lg p-8 hover:shadow-lg transition-all">
              <div className="flex justify-center mb-4">
                <div className="bg-secondary w-16 h-16 rounded-full flex items-center justify-center">
                  <TrendingUp className="text-white" size={32} />
                </div>
              </div>
              <div className="text-4xl font-bold text-primary mb-2">{trustSignals.clientsServed}</div>
              <div className="text-gray-600 font-medium">Satisfied Clients Nationwide</div>
            </div>

            <div className="text-center bg-accent/30 rounded-lg p-8 hover:shadow-lg transition-all">
              <div className="flex justify-center mb-4">
                <div className="bg-secondary w-16 h-16 rounded-full flex items-center justify-center">
                  <Award className="text-white" size={32} />
                </div>
              </div>
              <div className="text-4xl font-bold text-primary mb-2">{trustSignals.yearsExperience}</div>
              <div className="text-gray-600 font-medium">Years of Industry Expertise</div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="bg-primary text-white rounded-lg overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12">
                <h3 className="text-2xl md:text-3xl font-bold mb-8 text-white">
                  Recognized for Excellence in Trademark Services
                </h3>
                
                <div className="grid grid-cols-1 gap-6 mb-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="flex items-center space-x-4">
                      <Award className="text-secondary flex-shrink-0" size={40} />
                      <div>
                        <h4 className="font-bold text-lg mb-2 text-white">USPTO Filing Experts</h4>
                        <p className="text-sm text-gray-300">Specialized expertise in federal trademark registration procedures</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="flex items-center space-x-4">
                      <Shield className="text-secondary flex-shrink-0" size={40} />
                      <div>
                        <h4 className="font-bold text-lg mb-2 text-white">Trusted by Businesses</h4>
                        <p className="text-sm text-gray-300">Protecting brands across all industries and business sizes</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="flex items-center space-x-4">
                      <CheckCircle className="text-secondary flex-shrink-0" size={40} />
                      <div>
                        <h4 className="font-bold text-lg mb-2 text-white">Highest Success Rate</h4>
                        <p className="text-sm text-gray-300">95% approval rate significantly above industry average</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-gray-300 mb-6">
                    Join thousands of businesses that have trusted us to protect their most valuable asset—their brand identity.
                  </p>
                  <button
                    onClick={onOpenForm}
                    className="bg-secondary text-white px-8 py-4 rounded-md hover:bg-secondary/90 transition-all font-semibold shadow-lg text-lg"
                  >
                    Start Your Trademark Registration
                  </button>
                </div>
              </div>
              
              <div className="hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1755551636128-2385f552d7ec"
                  alt="Professional conference room"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Additional Trust Elements */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-accent/30 rounded-lg">
              <div className="text-2xl font-bold text-secondary mb-1">100%</div>
              <div className="text-xs text-gray-600">Satisfaction Guarantee</div>
            </div>
            <div className="text-center p-4 bg-accent/30 rounded-lg">
              <div className="text-2xl font-bold text-secondary mb-1">24/7</div>
              <div className="text-xs text-gray-600">Client Support</div>
            </div>
            <div className="text-center p-4 bg-accent/30 rounded-lg">
              <div className="text-2xl font-bold text-secondary mb-1">Secure</div>
              <div className="text-xs text-gray-600">Data Protection</div>
            </div>
            <div className="text-center p-4 bg-accent/30 rounded-lg">
              <div className="text-2xl font-bold text-secondary mb-1">Fast</div>
              <div className="text-xs text-gray-600">Processing Time</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
